import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    addProductsToGroup,
    changeActiveSelectionlist,
    changeListVisibility,
    createSelectionlist,
    deleteSelectionlist,
    duplicateList,
    fetchUnavailableProducts,
    importBulkISBN,
    removeProductFromList,
    removeProductsFromList,
    sendForApproval,
    setActiveList
} from '../../store/orderlist/actions';
import { changeAmountGroupedItem, deleteSelectionlistGroupedItem } from '../../store/orderlist/actionsGrouping';
import { copyAmountChange } from '../../store/copiesList/actions';
import DocumentTitle from '../../shared/components/DocumentTitle';
import {
    deselectAll,
    deselectOne,
    isInSelectionMode,
    isSelected,
    selectAll,
    selectOne,
    setSelectionMode
} from '../../store/productSelection/actions';
import { changeGroupBy, collapseGroups } from '../../store/grouping/actions';
import * as types from '../../store/constants';
import { GroupBy } from '../../store/constants';
import { displaySelectionlist } from '../../store/header/actions';
import { addToGroup } from '../../store/productGroup/actions';
import { UpdateProductOptions } from '../../store/products/actionsCart';
import { AppState } from '../../store/appState';
import { ListProduct, ProductMetaData, ProductSorting, UsageCondition } from '../../../models/Product';
import { Orderlist } from '../../../models/OrderList';
import { getGroupItemId, GroupItem } from '../../../models/GroupItem';
import { Article, SubscriptionPeriodType } from '../../../models/Article';
import { PriceTier } from '../../../models/PriceTier';
import { useHistory, useLocation, useParams } from 'react-router';
import { invalidateSelectionListProducts, useSelectionListProducts } from './api/useSelectionListProducts';
import queryString from 'query-string';
import { useQueryParams } from '../../shared/hooks/useQueryParams';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import { LoadingWrapper } from '../../shared/components/LoadingWrapper';
import LoadingOverlay from '../../shared/components/LoadingOverlay';
import { SelectionListHeader } from './header/SelectionListHeader';
import FilterBar from '../../shared/common/FilterBar';
import GroupedByList from '../../shared/common/GroupedByList';
import GroupedByBundleItem from '../../shared/bundles/GroupedByItem';
import SelectionlistProductTile from './SelectionlistProductTile';
import SelectionlistListItem from './SelectionlistListItem';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../shared/components/NoElementsFound';
import ProductList from '../../shared/common/ProductList';
import Pagination, { getPaginationStateObject } from '../../shared/common/Pagination';
import { SelectionlistCommentsBar } from './SelectionlistCommentsBar';
import { ContainerInnerWithColumns } from '../../shared/common/ContainerInner';
import { modifyURLState, modifyURLStates } from '../../shared/utils/utils-routing';
import { AppDispatch } from '../../store';
import { LIST_SORTING } from '../../shared/common/SortByDropDown';
import { isAdmin } from '../../store/authentication/actions';
import { useDownloadCSV } from '../../api/selection-list/useDownloadCSV';
import { useDownloadPdf } from '../../api/selection-list/useDownloadPdf';
import {
    invalidateSelectionListSummary,
    useSelectionListSummary
} from '../../api/selection-list/useSelectionListSummary';
import { useUpdateSelectionList } from './header/hooks/selectionListHooks';
import { ResponsiveFilterBar } from '../../shared/filter/ResponsiveFilterBar';
import { fetchSelectionListAggregationsQuickSearch } from '../../api/selection-list/fetchSelectionListAggregationsQuicksearch';
import { DisplayTypeState } from '../../store/displayType/state';
import Base64 from '../../shared/utils/base64';
import {
    invalidateSelectionListGroupedItems,
    useSelectionListGroupedItems
} from '../../api/selection-list/useSelectionListGroupedItems';
import { useBulkChangeSubscriptionPeriod } from './useBulkChangeSubscriptionPeriod';
import { useLibraryManagedProductGroups } from '../../yourborrowbox/marketing/productgroups/api/useLibraryManagedProductGroupsFeaturePages';
import { AuthenticationState } from '../../store/authentication/state';
import { FetchAggregationsQuickSearch } from '../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../shared/filter/defaultVisibleFilterGroups';
import { invalidateSelectionListAggregations, useSelectionListAggregations } from './useSelectionListAggregations';
import { disableListLoading } from '../../store/listLoading/actions';
import { useBulkUpdateUsageConditions } from '../../api/selection-list/bulkUpdateUsageConditions';
import { CommentsProvider } from './CommentsProvider';
import { useTranslation } from 'react-i18next';
import { useCopyBulkToSelectionList } from '../../api/selection-list/useCopyToList';
import { ListSourceType } from '../../../models/ListSourceType';
import { convertSelectionState } from '../../shared/product-selection/ProductSelectionTypes';
import { useCopyBulkToCart } from '../../api/shopping-cart/useCopyBulkToCart';
import { useMoveBulkToSelectionList } from '../../api/selection-list/useMoveToList';
import { useChangePriceInList } from '../../api/selection-list/useChangePriceInList';
import { useCopyBundleGroupToCart } from '../../api/shopping-cart/useCopyBundleGroupToCart';
import { useCopyBundleGroupToSelectionList } from '../../api/selection-list/useCopyBundleGroupToList';
import { useMoveBundleGroupToSelectionList } from '../../api/selection-list/useMoveBundleGroupToList';
import { useCopyToCart } from '../../api/shopping-cart/useCopyToCart';
import { useCopySingleToSelectionList } from '../../api/selection-list/useCopySingleToList';
import { useMoveSingleToSelectionList } from '../../api/selection-list/useMoveSingleToList';
import {
    isSingleCopyOptions,
    isSingleMoveOptions,
    SingleAddOptions,
    SingleCopyOrMoveOptions
} from '../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../shared/common/CheckBeforeAddingModal';

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';
const DEFAULT_PAGE_SIZE = 54;

export const SelectionListPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const history = useHistory();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();
    const orderlist = useSelector((state: AppState) => state.orderlist);
    const authentication = useSelector((state: AppState) => state.authentication);
    const spoc = useSelector((state: AppState) => state.authentication.spoc);
    const productSelection = useSelector((state: AppState) => state.productSelection);
    const isSticky = useSelector((state: AppState) => state.header.isSticky) ?? false;
    const productGroups = useLibraryManagedProductGroups();
    const isListLoading = useSelector((state: AppState) => state.listLoading.loading);

    const { groupBy: groupByQueryParam } = queryString.parse(location.search);

    const parsedGrouping: { key: GroupBy; id: string | null } = useMemo(
        () => (!groupByQueryParam ? { key: types.GROUPBY_PRODUCTS } : JSON.parse(Base64.decode(groupByQueryParam))),
        [groupByQueryParam]
    );

    const isGroupedBy = parsedGrouping.key !== types.GROUPBY_PRODUCTS;
    const isNotGroupExpanded = isGroupedBy && !parsedGrouping.id;

    const { displayFilter: displayFilterQueryParam } = queryString.parse(location.search);
    const displayFilter = displayFilterQueryParam === null;

    useLayoutEffect(() => {
        dispatch(displaySelectionlist());
        dispatch(disableListLoading());
    }, [dispatch]);

    useEffect(() => {
        if (isGroupedBy) {
            dispatch(changeGroupBy(parsedGrouping));
        }
    }, [dispatch, isGroupedBy, parsedGrouping]);

    useLayoutEffect(() => {
        dispatch(changeActiveSelectionlist(id));

        return () => {
            dispatch(changeActiveSelectionlist(undefined));
        };
    }, [dispatch, id]);

    const { data: listSummary, isInitialLoading: isLoadingSummary } = useSelectionListSummary(id);

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },
        filter: { value: filterQueryParam },
        display: { value: display }
    } = useQueryParams({
        [PAGE_QUERY_PARAM]: 'integer',
        [SORT_BY_QUERY_PARAM]: 'string',
        pageSize: 'integer',
        filter: 'string',
        display: 'string'
    });

    const page = pageParam - 1 ?? 0;
    const sortBy = sortByParam ?? ProductSorting.RELEASE_DATE_NEW_TO_OLD;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const filter = deserializeFilter(filterQueryParam);

    const { products, isLoading: isSelectionListProductsIsLoading } = useSelectionListProducts({
        filterMap: filter,
        pagination: { sortBy, page, pageSize },
        listId: id,
        groupType: parsedGrouping.key,
        groupId: parsedGrouping.id
    });

    const { data: groupedItems, isInitialLoading: isGroupedItemsLoading } = useSelectionListGroupedItems({
        listId: id,
        enabled: isGroupedBy
    });

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(() => selectionListPageFilterGroups(authentication), [authentication]);
    const { aggregationsQueryKeyBuilder, fetchAggregations } = useSelectionListAggregations(id, parsedGrouping);
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options => fetchSelectionListAggregationsQuickSearch({ ...options, listId: id }),
        [id]
    );
    //endregion

    const { mutate: updateSelectionListName, isLoading: isLoadingSelectionListName } = useUpdateSelectionList(id);

    const { mutate: changeBulkSubscriptionPeriod, isLoading: isLoadingChangeBulkSubscriptionPeriod } =
        useBulkChangeSubscriptionPeriod(id, filterQueryParam, convertSelectionState(productSelection, products.count));

    const { mutate: downloadCSV } = useDownloadCSV({ id, name: listSummary?.listName ?? 'selectionList' });
    const { mutate: downloadPdf } = useDownloadPdf({ id, name: listSummary?.listName ?? 'selectionList' });

    const { copyToListBulk: copyBulkProductsToList } = useCopyBulkToSelectionList({
        filter: filterQueryParam ?? '',
        source: ListSourceType.SELECTION_LIST,
        sourceSelectionListId: id
    });

    const { isLoading: isLoadingMoveToList, moveToListBulk: moveBulkProductsToList } = useMoveBulkToSelectionList({
        filter: filterQueryParam ?? '',
        source: ListSourceType.SELECTION_LIST,
        sourceSelectionListId: id
    });

    const { copyToCartBulk: copyBulkProductsToCart } = useCopyBulkToCart({
        filter: filterQueryParam ?? '',
        sourceSelectionListId: id,
        callback: () => {
            invalidateSelectionListProducts(id).then();
        }
    });

    const onBulkCopyToList = (targetList: Orderlist, performChecks: boolean) => {
        return copyBulkProductsToList(
            targetList.id,
            performChecks,
            convertSelectionState(productSelection, products.count)
        ).then(data => {
            if (!performChecks) {
                dispatch(setActiveList(targetList));
            }
            return data;
        });
    };

    const onBulkMoveToList = (targetList: Orderlist, performChecks: boolean) => {
        return moveBulkProductsToList(
            targetList.id,
            performChecks,
            convertSelectionState(productSelection, products.count)
        ).then(data => {
            if (!performChecks) {
                dispatch(setActiveList(targetList));
            }
            return data;
        });
    };

    const onBulkCopyToCart = (performChecks: boolean) => {
        return copyBulkProductsToCart(performChecks, convertSelectionState(productSelection, products.count));
    };

    function handleFetchUnavailableProducts() {
        dispatch(fetchUnavailableProducts(id));
    }

    function onDisplayChange(nextDisplayType) {
        const nextSearch = modifyURLState(history.location.search, 'display', nextDisplayType, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    const setPage = nextPage => {
        const nextSearch = modifyURLState(
            history.location.search,
            PAGE_QUERY_PARAM,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: SORT_BY_QUERY_PARAM, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(history.location.search, [pageQueryState, sortByQueryState]);

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(history.location.search, 'displayFilter', !displayFilter, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function onGroupBy(group) {
        dispatch(changeGroupBy(group));
    }

    const { addToCart: copyToCart } = useCopyToCart(id, () => {
        invalidateSelectionListProducts(id).then();
    });

    const { copyToList } = useCopySingleToSelectionList({
        source: ListSourceType.SELECTION_LIST,
        sourceSelectionListId: id
    });

    const { moveToList } = useMoveSingleToSelectionList({
        source: ListSourceType.SELECTION_LIST,
        sourceSelectionListId: id
    });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions | SingleCopyOrMoveOptions) => {
            if (isSingleCopyOptions(options) && options.target === ListActionTarget.List) {
                return copyToList(options.options, options.listId, performChecks);
            } else if (isSingleMoveOptions(options) && options.target === ListActionTarget.List) {
                return moveToList(options.options, options.listId, performChecks);
            } else {
                return copyToCart(options.options, performChecks);
            }
        },
        [copyToCart, copyToList, moveToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps } = useListContentChecks<
        SingleAddOptions | SingleCopyOrMoveOptions
    >({ onAction });

    const { changePriceBulk } = useChangePriceInList({
        filter: filterQueryParam ?? ''
    });

    function onChangePrice(priceTier: PriceTier, performChecks: boolean) {
        return changePriceBulk(priceTier, id, performChecks, convertSelectionState(productSelection, products.count));
    }

    const { copyToCartBulk: copyBundleGroupToCart } = useCopyBundleGroupToCart(() => {
        dispatch(collapseGroups());
        invalidateSelectionListProducts(id).then();
    });

    function onCopyGroupToCart(bundleId: string, performChecks: boolean) {
        return copyBundleGroupToCart(id, bundleId, performChecks);
    }

    function onRemoveProductFromList(productIdOrOptions: string | UpdateProductOptions) {
        const scrollY = window.scrollY;
        dispatch(removeProductFromList(productIdOrOptions, id)).then(() => {
            if (listSummary) {
                invalidateSelectionListProducts(id).then();
                invalidateSelectionListAggregations(id).then();
                invalidateSelectionListSummary(id).then();
                invalidateSelectionListGroupedItems(id).then();

                window.requestAnimationFrame(() => {
                    window.scrollTo(0, scrollY);
                });
                dispatch(setActiveList(listSummary));
            }
        });
    }

    function onRemoveFromList() {
        if (listSummary) {
            dispatch(removeProductsFromList())?.then(() => {
                invalidateSelectionListProducts(id).then();
                invalidateSelectionListAggregations(id).then();
                invalidateSelectionListSummary(id).then();
                invalidateSelectionListGroupedItems(id).then();
            });
            dispatch(setActiveList(listSummary));
        }
    }

    function onCopyAmountChange(
        productIdOrOptions: string | UpdateProductOptions | null,
        amount: number,
        command: string = types.ACTION_SET
    ) {
        if (listSummary) {
            dispatch(copyAmountChange(productIdOrOptions, amount, command, true));
            dispatch(setActiveList(listSummary));
        }
    }

    function handleDeselectAll() {
        dispatch(deselectAll());
    }

    function onRemoveGroupedItemFromList(item: GroupItem) {
        const group = { ...parsedGrouping, id: getGroupItemId(item) };
        dispatch(deleteSelectionlistGroupedItem(group));
    }

    const { copyToListBulk: copyBundleGroupToList } = useCopyBundleGroupToSelectionList({
        source: ListSourceType.SELECTION_LIST,
        sourceSelectionListId: id
    });

    function onCopyBundleGroupToList(item: GroupItem, list: Orderlist, performChecks: boolean) {
        return copyBundleGroupToList(getGroupItemId(item), list.id, performChecks);
    }

    const { isLoading: isLoadingMoveGroupToList, moveToListBulk: moveBundleGroupToList } =
        useMoveBundleGroupToSelectionList({
            source: ListSourceType.SELECTION_LIST,
            sourceSelectionListId: id
        });

    function onMoveBundleGroupToList(item: GroupItem, list: Orderlist, performChecks: boolean) {
        return moveBundleGroupToList(getGroupItemId(item), list.id, performChecks);
    }
    function onGroupAmountChange(item: GroupItem, amount: number, command: string) {
        const group = { ...parsedGrouping, id: getGroupItemId(item) };
        dispatch(changeAmountGroupedItem(group, amount, command));
    }

    function handleProductSelection(productId) {
        const selected = isSelected(productId, productSelection);
        if (!selected) {
            dispatch(selectOne(productId));
        } else {
            dispatch(deselectOne(productId));
        }
    }

    function onISBNBulkImport(isbns: string) {
        return dispatch(importBulkISBN(isbns));
    }

    const bulkUpdateUsageConditions = useBulkUpdateUsageConditions(id);

    async function onUpdateUsageCondition(
        product: ProductMetaData & Article,
        usageCondition: UsageCondition,
        subscriptionPeriodType: SubscriptionPeriodType | undefined = undefined
    ) {
        const sourceUsageCondition = product.price
            ? {
                  lendingModel: product.price.lendingModel,
                  usageLimit: product.price.usageLimit,
                  termLimit: product.price.termLimit
              }
            : {};
        const sourceSubscriptionPeriodType = product?.subscriptionPeriod?.type;
        const targetSubscriptionPeriodType = subscriptionPeriodType ?? product?.subscriptionPeriod?.type;

        await bulkUpdateUsageConditions([
            {
                productId: product.id,
                sourceUsageCondition: sourceUsageCondition,
                targetUsageCondition: usageCondition,
                sourceSubscriptionPeriodType,
                targetSubscriptionPeriodType
            }
        ]);
    }

    const loading =
        isSelectionListProductsIsLoading ||
        isLoadingSummary ||
        isLoadingSelectionListName ||
        isGroupedItemsLoading ||
        isLoadingChangeBulkSubscriptionPeriod ||
        isListLoading ||
        isLoadingMoveToList ||
        isLoadingMoveGroupToList;

    return (
        <CommentsProvider>
            <DocumentTitle title={t('components.documentTitle.selectionlists')} />
            {id ? (
                <ContainerInnerWithColumns isLoading={loading}>
                    {loading ? (
                        <LoadingWrapper>
                            <LoadingOverlay loading={loading} />
                        </LoadingWrapper>
                    ) : null}
                    <>
                        <SelectionListHeader
                            authentication={authentication}
                            isAdmin={isAdmin(authentication)}
                            isInSelectionMode={isInSelectionMode(productSelection)}
                            productGroups={productGroups}
                            selectionListBarExpanded={true}
                            spoc={spoc}
                            isSticky={isSticky}
                            sendForApprovalErrorMsg={orderlist.errorMsg}
                            pendingApprovalRequest={orderlist.pendingApprovalRequest}
                            orderlist={listSummary ?? {}}
                            productSelection={productSelection}
                            fetchUnavailableProducts={handleFetchUnavailableProducts}
                            addListToCart={onBulkCopyToCart}
                            cancelSelectionMode={() => dispatch(setSelectionMode(false))}
                            deselectAll={handleDeselectAll}
                            onBulkAddToCart={onBulkCopyToCart}
                            onBulkAddToGroup={productGroupId => dispatch(addProductsToGroup(id, productGroupId))}
                            onChangeListVisibility={(listId, visibilityState) =>
                                dispatch(changeListVisibility(listId, visibilityState))
                            }
                            onCopyAmountChange={onCopyAmountChange}
                            onChangePrice={onChangePrice}
                            onCopyToList={onBulkCopyToList}
                            onMoveToList={onBulkMoveToList}
                            onRemoveFromList={onRemoveFromList}
                            onSendForApproval={(listId, email) => dispatch(sendForApproval(listId, email))}
                            onDownloadCSV={downloadCSV}
                            onDownloadPdf={downloadPdf}
                            onDuplicateList={() => dispatch(duplicateList())}
                            onISBNBulkImport={onISBNBulkImport as any}
                            onDelete={() => dispatch(deleteSelectionlist(id))}
                            onSaveSelectionlistName={updateSelectionListName}
                            onChangeBulkSubscriptionPeriod={changeBulkSubscriptionPeriod}
                            hasActiveFilter={!!filterQueryParam}
                        />
                        <FilterBar
                            toggleSelectionMode={() => dispatch(setSelectionMode(!isInSelectionMode(productSelection)))}
                            selectAll={() => dispatch(selectAll(products.count))}
                            onFilterDisplayToggle={onFilterDisplayToggle}
                            displayType={display as any}
                            onGroupBy={onGroupBy}
                            hidePagination={isGroupedBy}
                            hideSelectButtons={isNotGroupExpanded}
                            hideSelectAll={isGroupedBy}
                            totalProducts={products.count}
                            grouping={parsedGrouping}
                            onDisplayChange={onDisplayChange}
                            sortBy={sortBy}
                            onChangePrice={onChangePrice}
                            sorting={LIST_SORTING}
                            onSortChange={setSortBy}
                            onPageChange={setPage}
                            currentPage={page}
                            pageSize={pageSize}
                            dispatch={dispatch}
                            productSelection={productSelection}
                            displayFilter={displayFilter}
                        />
                    </>
                    <div className="flex-ca" style={{ alignItems: 'flex-start' }}>
                        {displayFilter ? (
                            <ResponsiveFilterBar
                                visibleFilterGroups={visibleFilterGroups}
                                buildQueryKey={aggregationsQueryKeyBuilder}
                                fetchAggregations={fetchAggregations}
                                fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                            />
                        ) : null}

                        <div className="ca-main">
                            {isGroupedBy ? (
                                groupedItems && groupedItems.length > 0 ? (
                                    <div className="ca-main-inner groupby-sl" key={`${parsedGrouping.key}_group`}>
                                        <GroupedByList
                                            pageSize={pageSize}
                                            currentPage={page}
                                            totalProducts={products.count}
                                            products={products?.list as ListProduct[]}
                                            productSelection={productSelection}
                                            onCopyAmountChange={onCopyAmountChange}
                                            dispatch={dispatch}
                                            displayType={display as DisplayTypeState}
                                            groups={groupedItems as unknown as ReadonlyArray<GroupItem>}
                                            onSelectProduct={handleProductSelection}
                                            isGroupAddAction={true}
                                            itemComponent={GroupedByBundleItem}
                                            tileItemComponent={SelectionlistProductTile}
                                            listItemComponent={SelectionlistListItem}
                                            grouping={parsedGrouping}
                                            onPageChange={setPage}
                                            onAddPrice={changePrice}
                                            deselectAll={handleDeselectAll}
                                            onMoveGroupToList={onMoveBundleGroupToList}
                                            onGroupAmountChange={onGroupAmountChange}
                                            onUpdateUsageCondition={onUpdateUsageCondition}
                                            onRemoveProductFromList={onRemoveProductFromList}
                                            onRemoveGroupedItemFromList={onRemoveGroupedItemFromList}
                                            onCopyGroupToList={onCopyBundleGroupToList}
                                            onAddGroupedItemToCart={onCopyGroupToCart}
                                            onCreateNewListClicked={newSelectionListName =>
                                                dispatch(createSelectionlist(newSelectionListName))
                                            }
                                            invalidateAggregations={() => invalidateSelectionListAggregations(id)}
                                            currentListId={id}
                                        />
                                    </div>
                                ) : !loading ? (
                                    <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                                ) : null
                            ) : null}

                            {!isGroupedBy ? (
                                products.count > 0 ? (
                                    <div className="ca-main-inner">
                                        <ProductList
                                            displayType={display}
                                            listItemComponent={SelectionlistListItem}
                                            tileItemComponent={SelectionlistProductTile}
                                            onSelectProduct={handleProductSelection}
                                            onRemoveGroupedItemFromList={onRemoveGroupedItemFromList}
                                            products={products.list}
                                            onUpdateUsageCondition={onUpdateUsageCondition}
                                            productSelection={productSelection}
                                            onCopyAmountChange={onCopyAmountChange}
                                            onAddToGroup={(productGroupId, productId) =>
                                                dispatch(addToGroup(productGroupId, productId))
                                            }
                                            onAddPrice={changePrice}
                                            onRemoveProductFromList={onRemoveProductFromList}
                                            currentPage={page}
                                            currentListId={id}
                                        />
                                        <Pagination
                                            totalItems={products.count}
                                            onPageChange={setPage}
                                            currentPage={page}
                                            pageSize={pageSize}
                                        />
                                    </div>
                                ) : !loading ? (
                                    <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                                ) : null
                            ) : null}
                        </div>

                        <SelectionlistCommentsBar selectionListId={id} />
                    </div>
                    <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
                </ContainerInnerWithColumns>
            ) : null}
        </CommentsProvider>
    );
};

const selectionListPageFilterGroups = (authentication: AuthenticationState) => [
    ...defaultVisibleFilterGroups(authentication),
    'selectedLendingModel',
    'selectedUsageCondition',
    'selectedPrice'
];
